import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, LayoutGrid, List } from "lucide-react";
import { api } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { CatalogueCard } from "@/components/catalogue/CatalogueCard";
import { HERO_ALT } from "@/lib/content";

const FILTER_KEYS = [
  { key: "domain", label: "Domain", tax: "domains" },
  { key: "discipline", label: "Discipline", tax: "disciplines" },
  { key: "method", label: "Method", tax: "methods" },
  { key: "readiness", label: "Readiness", tax: "readiness" },
  { key: "output_type", label: "Output", tax: "output_types" },
  { key: "access", label: "Access", tax: "access" },
];

export default function Capabilities() {
  const [params, setParams] = useSearchParams();
  const [tax, setTax] = useState({});
  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [q, setQ] = useState(params.get("q") || "");

  useEffect(() => {
    api.get("/taxonomy").then(({ data }) => setTax(data));
  }, []);

  const activeFilters = useMemo(() => {
    const obj = {};
    FILTER_KEYS.forEach((f) => {
      const v = params.get(f.key);
      if (v) obj[f.key] = v;
    });
    if (params.get("q")) obj.q = params.get("q");
    return obj;
  }, [params]);

  useEffect(() => {
    setLoading(true);
    api.get("/capabilities", { params: activeFilters }).then(({ data }) => {
      setCaps(data.results);
      setLoading(false);
    });
  }, [activeFilters]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || next.get(key) === value) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q.trim()) next.set("q", q.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setQ("");
  };

  const chips = Object.entries(activeFilters);

  return (
    <>
      <PageMeta title="Capabilities" description="A searchable catalogue of DBRF capabilities, organised by what a project may need." />
      <PageHero
        eyebrow="Resource catalogue"
        title="Capabilities that connect across the problem."
        intro="The catalogue is organised by what a project may need — not by a fixed departmental structure. Search methods, disciplines, technical assets and output types, then examine how they can combine."
        image="/assets/03-capabilities-hero.webp"
        alt={HERO_ALT.capabilities}
        objectPosition="center 30%"
      />

      <section className="container-dbrf py-14 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[280px_1fr]">
          {/* Filters */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="catalogue-filters">
            <form onSubmit={submitSearch} className="relative mb-6">
              <label htmlFor="catalogue-search" className="sr-only">Search the capability catalogue</label>
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mineral)]" />
              <input
                id="catalogue-search"
                data-testid="catalogue-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search catalogue"
                className="w-full rounded-full border border-[var(--line)] bg-[var(--paper)] py-2.5 pl-9 pr-20 text-sm outline-none focus:border-[var(--cobalt)]"
              />
              <button type="submit" data-testid="catalogue-search-btn" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white btn-transition hover:bg-[var(--cobalt)]">Search</button>
            </form>
            {FILTER_KEYS.map((f) => (
              <div key={f.key} className="mb-5">
                <p className="eyebrow mb-2">{f.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(tax[f.tax] || []).map((opt) => {
                    const isActive = params.get(f.key) === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => setFilter(f.key, opt)}
                        aria-pressed={isActive}
                        data-testid={`filter-${f.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`btn-transition rounded-full border px-2.5 py-1 text-xs ${
                          isActive
                            ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white"
                            : "border-[var(--line)] bg-[var(--paper)] text-navy hover:border-[var(--cobalt)]"
                        }`}
                      >
                        {isActive ? "✓ " : ""}{opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          {/* Results */}
          <div>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span data-testid="result-count" className="font-mono text-sm text-[var(--mineral)]" aria-live="polite">
                  {loading ? "Searching…" : `${caps.length} result${caps.length === 1 ? "" : "s"}`}
                </span>
                {chips.length > 0 && (
                  <button onClick={clearAll} data-testid="clear-all-filters" className="text-xs font-semibold text-[var(--cobalt)] link-underline">
                    Clear all
                  </button>
                )}
              </div>
              <div className="flex gap-1 rounded-full border border-[var(--line)] p-1">
                <button onClick={() => setView("grid")} data-testid="view-grid" aria-label="Grid view" className={`rounded-full p-1.5 ${view === "grid" ? "bg-[var(--ivory)] text-[var(--cobalt)]" : "text-[var(--mineral)]"}`}>
                  <LayoutGrid size={16} />
                </button>
                <button onClick={() => setView("list")} data-testid="view-list" aria-label="List view" className={`rounded-full p-1.5 ${view === "list" ? "bg-[var(--ivory)] text-[var(--cobalt)]" : "text-[var(--mineral)]"}`}>
                  <List size={16} />
                </button>
              </div>
            </div>

            {chips.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-2" data-testid="active-filter-chips">
                {chips.map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => (k === "q" ? (setQ(""), setFilter("q", null)) : setFilter(k, null))}
                    data-testid={`chip-${k}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[var(--navy)] px-3 py-1 text-xs text-white"
                  >
                    {v} <X size={12} />
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="grid gap-6 sm:grid-cols-2">
                {[0, 1, 2, 3].map((i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-[var(--paper)]" />)}
              </div>
            ) : caps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-12 text-center" data-testid="catalogue-empty">
                <p className="font-display text-lg text-navy">No capabilities match these filters.</p>
                <p className="mt-2 text-sm text-[var(--mineral)]">Try removing a filter or clearing all.</p>
              </div>
            ) : (
              <div className={view === "grid" ? "grid gap-6 sm:grid-cols-2" : "flex flex-col gap-4"}>
                {caps.map((cap) => <CatalogueCard key={cap.slug} cap={cap} compact={view === "list"} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
